// Piezas compartidas del cobro en renglones fijos y del reparto entre tarjetas.
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { AmountInput } from './AmountInput';

export const RECEIPT_INPUT =
    'block h-8 rounded-md border border-input bg-background px-2 text-[12px] leading-[1.4] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_2px_hsl(var(--ring)/0.15)]';

export const ERROR_TEXT = 'text-[11px] leading-[1.35] text-destructive';

/** Deja solo los caracteres permitidos, con tope de largo. */
export function sanitize(value: string, disallowed: RegExp, maxLength: number): string {
    return value.replace(disallowed, '').slice(0, maxLength);
}

/** Monto de un renglón: se escribe libre y el reparto se aplica al salir del campo o con Enter. */
export function RowAmount({
    cents,
    label,
    disabled = false,
    readOnly = false,
    onCommit,
}: {
    cents: number;
    label: string;
    disabled?: boolean;
    readOnly?: boolean;
    onCommit: (cents: number) => void;
}) {
    const [pending, setPending] = useState<number | null>(null);
    if (readOnly) {
        return (
            <span
                aria-label={label}
                className="w-[120px] flex-none text-right text-[14px] font-semibold tabular-nums"
            >
                {formatMoneyCents(cents)}
            </span>
        );
    }
    return (
        <div className="relative flex-none">
            <span
                aria-hidden
                className={cn(
                    'pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[13px]',
                    disabled ? 'text-muted-foreground/50' : 'text-muted-foreground',
                )}
            >
                $
            </span>
            <AmountInput
                aria-label={label}
                value={pending ?? cents / 100}
                disabled={disabled}
                onValueChange={setPending}
                onCommit={() => {
                    if (pending === null) return;
                    const next = Math.round(pending * 100);
                    setPending(null);
                    if (next !== cents) onCommit(next);
                }}
                className="h-9 w-[120px] rounded-md border-input pl-5 pr-2 text-right text-[14px] font-semibold"
            />
        </div>
    );
}

/** Botón cuadrado de icono (candado y «todo aquí»). */
export function IconButton({
    label,
    onClick,
    pressed,
    children,
}: {
    label: string;
    onClick: () => void;
    /** Solo para botones que se prenden y apagan. */
    pressed?: boolean;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            aria-label={label}
            aria-pressed={pressed}
            className={cn(
                'flex size-9 flex-none items-center justify-center rounded-md border transition-colors',
                pressed
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
        >
            {children}
        </button>
    );
}
